/**
 * Backups page (OCP-380, source + naming fixed in OCP-529).
 *
 * The customer's managed backups: the orboto app's hourly FULL-EXPORT ZIP
 * (manifest + per-entity NDJSON, ORB-1266), written to the managed central
 * backup storage and listed through the control plane. Download = data
 * portability (the Terms export path) + the floor of the "on-demand
 * restore" promise. One-click restore-into-instance is a follow-up.
 *
 * Three distinct empty-ish states, deliberately not collapsed into one:
 *  - managed backup NOT configured -> say so; an empty list here would read
 *    like data loss on the one surface that promises the opposite;
 *  - configured but nothing written yet -> "no backups yet";
 *  - configured but storage unreadable for an instance -> "temporarily
 *    unavailable", per instance.
 *
 * Downloads are owner / admin only (OCP-929): an archive is the complete
 * data of the workspace. Every other role sees the status (sizes, times, the
 * overdue alarm) without archive names or download actions, with the
 * read-only notice; a 403 that still happens (role changed while the page
 * was open) is explained, never shown raw.
 */
import { useCallback, useEffect, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { usePortalAccess } from '../lib/use-portal-access';
import { RoleReadOnlyNotice } from '../components/RoleReadOnlyNotice';

import { api, ApiError } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';
import { fmtNumber } from '../lib/format-money';

interface BackupRow {
  tenant_id: string;
  subdomain: string;
  /** Key relative to the workspace's backup prefix - a bare filename. Null when this member may not download (OCP-929). */
  key: string | null;
  size_bytes: number;
  last_modified: string | null;
}

interface UnavailableInstance {
  tenant_id: string;
  subdomain: string;
}

/**
 * OCP-539 - an instance past the grace window with zero archives. Kept
 * separate from `unavailable` (a transient read failure) because the two need
 * opposite messages: one says "try again shortly", this one says "something is
 * wrong and we need to hear about it".
 */
interface OverdueInstance {
  tenant_id: string;
  subdomain: string;
  since: string;
}

/** OCP-616 - one-off full export (database + files) archive under exports/. */
interface ExportRow {
  tenant_id: string;
  subdomain: string;
  /** Null when this member may not download (OCP-929). */
  key: string | null;
  size_bytes: number;
  last_modified: string | null;
}

interface ExportRequestRow {
  tenant_id: string;
  subdomain: string;
  id: string;
  status: 'requested' | 'delivered' | 'running' | 'ok' | 'failed' | string;
  requested_at: string;
  delivered_at: string | null;
  result_at: string | null;
  bytes: number | null;
  key: string | null;
  error: string | null;
}

interface ExportTenantRow {
  tenant_id: string;
  subdomain: string;
  can_request: boolean;
  reason: 'not_configured' | 'tenant_deleting' | 'request_open' | 'not_wired' | null;
  storage_total_gb: number | null;
  open_request_id: string | null;
}

interface BackupsResponse {
  managed_backup_configured: boolean;
  /** OCP-929 - false for roles that may not download (viewer, billing-only). */
  can_download?: boolean;
  backups: BackupRow[];
  unavailable: UnavailableInstance[];
  overdue?: OverdueInstance[];
  /** OCP-616 */
  exports?: ExportRow[];
  export_requests?: ExportRequestRow[];
  export_tenants?: ExportTenantRow[];
  /** OCP-659 - AGB V4 12.3: days the export stays reachable after the contract ends. */
  export_grace_days?: number;
}

/** Statuses with a label in `backups:export.status.<key>` (OCP-656). */
const EXPORT_STATUSES = new Set(['requested', 'delivered', 'running', 'ok', 'failed']);

type Translate = (key: string, opts?: Record<string, unknown>) => string;

/** Fallback download filename - the artifact is a full-export ZIP. */
const FALLBACK_FILENAME = 'orboto-export.zip';

function fmtSize(bytes: number, t: Translate): string {
  const one = { minimumFractionDigits: 1, maximumFractionDigits: 1 };
  if (bytes < 1024) return t('size.b', { n: fmtNumber(bytes) });
  if (bytes < 1024 * 1024) return t('size.kb', { n: fmtNumber(bytes / 1024, one) });
  return t('size.mb', { n: fmtNumber(bytes / 1024 / 1024, one) });
}

export function BackupsPage() {
  const { t } = useTranslation('backups');
  const [rows, setRows] = useState<BackupRow[]>([]);
  const [configured, setConfigured] = useState(true);
  const [unavailable, setUnavailable] = useState<UnavailableInstance[]>([]);
  const [overdue, setOverdue] = useState<OverdueInstance[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [exports, setExports] = useState<ExportRow[]>([]);
  const [exportRequests, setExportRequests] = useState<ExportRequestRow[]>([]);
  const [exportTenants, setExportTenants] = useState<ExportTenantRow[]>([]);
  const [exportGraceDays, setExportGraceDays] = useState(90);
  const [requesting, setRequesting] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const { canManageInfra, canExportData, readOnlyReason } = usePortalAccess();
  // OCP-929 - the server's answer for THIS session wins over the role the
  // page was loaded with.
  const [serverCanDownload, setServerCanDownload] = useState(true);
  const canDownload = canExportData && serverCanDownload;

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await api<BackupsResponse>('/v1/portal/backups');
      setRows(r.backups);
      setConfigured(r.managed_backup_configured);
      setUnavailable(r.unavailable ?? []);
      setOverdue(r.overdue ?? []);
      setExports(r.exports ?? []);
      setExportRequests(r.export_requests ?? []);
      setExportTenants(r.export_tenants ?? []);
      setExportGraceDays(typeof r.export_grace_days === 'number' ? r.export_grace_days : 90);
      setServerCanDownload(r.can_download !== false);
    } catch (e) {
      setError(e instanceof ApiError ? t('loadFailedHttp', { status: e.status }) : t('loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);
  useEffect(() => {
    void load();
  }, [load]);

  async function download(row: BackupRow, id: string) {
    if (!row.key) return;
    const key = row.key;
    setDownloading(id);
    setError(null);
    try {
      const url = `/v1/portal/backups/download?tenant_id=${encodeURIComponent(row.tenant_id)}&key=${encodeURIComponent(key)}`;
      const res = await fetch(url, { credentials: 'include' });
      if (res.status === 403) {
        setServerCanDownload(false);
        setError(t('downloadForbidden'));
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = key.split('/').pop() || FALLBACK_FILENAME;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      setError(t('downloadFailed'));
    } finally {
      setDownloading(null);
    }
  }

  async function requestExport(row: ExportTenantRow) {
    setRequesting(row.tenant_id);
    setError(null);
    setNotice(null);
    try {
      await api('/v1/portal/backups/exports/request', { method: 'POST', body: { tenant_id: row.tenant_id } });
      setNotice(t('export.requested', { subdomain: row.subdomain }));
      await load();
    } catch (err) {
      const status = err instanceof ApiError ? err.status : 0;
      setError(status === 409 ? t('export.conflict') : status === 403 ? t('export.forbidden') : t('export.requestFailed'));
    } finally {
      setRequesting(null);
    }
  }

  const downloadHref = (row: { tenant_id: string; key: string }) =>
    `/v1/portal/backups/download?tenant_id=${encodeURIComponent(row.tenant_id)}&key=${encodeURIComponent(row.key)}`;

  /**
   * OCP-929 - a full export streams through a plain link (the browser can
   * resume a multi-GB archive). Before following it, re-ask the listing
   * whether THIS session may still download, so a member whose role was
   * lowered while the page was open gets the explanation instead of a raw
   * 403 body. The listing reads no archive and writes no audit row.
   */
  async function openExport(event: MouseEvent<HTMLAnchorElement>, href: string) {
    event.preventDefault();
    setError(null);
    try {
      const r = await api<BackupsResponse>('/v1/portal/backups');
      if (r.can_download === false) {
        setServerCanDownload(false);
        setError(t('downloadForbidden'));
        return;
      }
    } catch {
      // The link itself still answers; a listing blip must not block the export.
    }
    window.location.assign(href);
  }

  return (
    <section className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('title')}</h1>
        <p className="text-sm text-gray-500">{t('intro')}</p>
      </div>

      {!loading && configured && !canDownload && <RoleReadOnlyNotice scope="export" />}

      {error && (
        <div role="alert" className="rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">
          {error}
        </div>
      )}

      {!loading && !configured && (
        <div
          role="status"
          data-testid="backups-not-configured"
          className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 p-5 text-sm text-amber-900 dark:text-amber-200"
        >
          <p className="font-semibold">{t('notConfigured.title')}</p>
          <p className="mt-1">{t('notConfigured.body')}</p>
          <p className="mt-2">
            {t('notConfigured.contact')}{' '}
            <a href="mailto:support@orboto.io" className="font-medium underline">
              support@orboto.io
            </a>
          </p>
        </div>
      )}

      {unavailable.length > 0 && (
        <div
          role="status"
          data-testid="backups-unavailable"
          className="rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-900 dark:text-amber-200"
        >
          {t('unavailable', { instances: unavailable.map((u) => u.subdomain).join(', ') })}
        </div>
      )}

      {overdue.length > 0 && (
        <div
          role="alert"
          data-testid="backups-overdue"
          className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 p-5 text-sm text-red-800 dark:text-red-300"
        >
          <p className="font-semibold">
            {t('overdue.title', { instances: overdue.map((o) => o.subdomain).join(', ') })}
          </p>
          <p className="mt-1">{t('overdue.body')}</p>
          <p className="mt-2">
            {t('overdue.please')}{' '}
            <a href="mailto:support@orboto.io" className="font-medium underline">
              {t('overdue.contactSupport')}
            </a>{' '}
            {t('overdue.after')}
          </p>
        </div>
      )}

      {notice && (
        <div role="status" className="rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
          {notice}
        </div>
      )}

      {/* OCP-616 - the one-off full export (database + files), apart from the hourly series. */}
      {!loading && configured && exportTenants.length > 0 && (
        <section data-testid="backups-exports" className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('export.title')}</h2>
          <p className="mt-1 text-sm text-gray-500">{t('export.intro')}</p>
          <p className="mt-1 text-xs text-gray-500" data-testid="backups-export-retention">{t('export.retention', { days: exportGraceDays })}</p>
          {!canManageInfra && (
            <p className="mt-2 text-xs text-amber-800 dark:text-amber-300">{readOnlyReason('infra')}</p>
          )}
          <ul className="mt-3 space-y-2">
            {exportTenants.map((row) => {
              const open = exportRequests.find((r) => r.id === row.open_request_id) ?? null;
              const last = exportRequests.find((r) => r.tenant_id === row.tenant_id) ?? null;
              const shown = open ?? last;
              return (
                <li key={row.tenant_id} className="flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`export-tenant-${row.subdomain}`}>
                  <div>
                    <span className="font-medium text-gray-800 dark:text-gray-200">{row.subdomain}</span>
                    <span className="ml-2 text-xs text-gray-500">
                      {row.storage_total_gb !== null
                        ? t('export.sizeAbout', { gb: fmtNumber(row.storage_total_gb, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) })
                        : t('export.sizeUnknown')}
                    </span>
                    {shown && (
                      <div className="text-xs text-gray-500" data-testid={`export-status-${row.subdomain}`}>
                        {EXPORT_STATUSES.has(shown.status) ? t(`export.status.${shown.status}`) : shown.status}
                        {' · '}{t('export.requestedAt', { when: fmtDateTime(shown.requested_at) })}
                        {shown.status === 'failed' && shown.error ? ` · ${shown.error}` : ''}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    disabled={!row.can_request || !canManageInfra || requesting === row.tenant_id}
                    onClick={() => void requestExport(row)}
                    title={row.reason === 'request_open' ? t('export.alreadyRunningTitle') : row.reason === 'tenant_deleting' ? t('export.deletingTitle') : undefined}
                    className="rounded border border-gray-300 dark:border-gray-700 px-2.5 py-1 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
                  >
                    {requesting === row.tenant_id ? t('export.requesting') : row.reason === 'request_open' ? t('export.running') : t('export.request')}
                  </button>
                </li>
              );
            })}
          </ul>
          {exports.length > 0 && (
            <table className="mt-3 min-w-full text-sm" data-testid="exports-table">
              <thead className="text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="py-1 pr-3 text-left">{t('table.instance')}</th>
                  {canDownload && <th className="py-1 pr-3 text-left">{t('table.archive')}</th>}
                  <th className="py-1 pr-3 text-right">{t('table.size')}</th>
                  <th className="py-1 pr-3 text-left">{t('table.when')}</th>
                  {canDownload && <th className="py-1 pr-3 text-right">{t('table.action')}</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                {exports.map((e, i) => {
                  const key = e.key;
                  const href = key ? downloadHref({ tenant_id: e.tenant_id, key }) : null;
                  return (
                    <tr key={`${e.tenant_id}:${key ?? `${e.last_modified ?? ''}:${i}`}`} data-testid={`export-row-${e.subdomain}`}>
                      <td className="py-1 pr-3 text-gray-700 dark:text-gray-300">{e.subdomain}</td>
                      {canDownload && (
                        <td className="py-1 pr-3 font-mono text-xs text-gray-600 dark:text-gray-400">{key?.split('/').pop()}</td>
                      )}
                      <td className="py-1 pr-3 text-right tabular-nums text-gray-600 dark:text-gray-400">{fmtSize(e.size_bytes, t)}</td>
                      <td className="py-1 pr-3 text-xs text-gray-500">{fmtDateTime(e.last_modified)}</td>
                      {canDownload && (
                        <td className="py-1 pr-3 text-right">
                          {/* A direct link: the browser streams and can resume a multi-GB archive (HTTP Range). */}
                          {href && (
                            <a
                              href={href}
                              onClick={(ev) => void openExport(ev, href)}
                              className="rounded border border-gray-300 dark:border-gray-700 px-2.5 py-1 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
                            >
                              {t('export.downloadSize', { size: fmtSize(e.size_bytes, t) })}
                            </a>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>
      )}

      {loading ? (
        <p className="text-sm text-gray-500">{t('loading')}</p>
      ) : configured && rows.length === 0 ? (
        // Only the genuinely-too-early case gets the neutral message. An
        // instance past the grace window is covered by the alert above -
        // OCP-539, where this reassuring text was shown for three weeks to
        // customers who had no backup at all.
        overdue.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-8 text-center text-sm text-gray-500">
            {t('empty')}
          </div>
        ) : null
      ) : rows.length > 0 ? (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">{t('table.instance')}</th>
                {canDownload && <th className="px-3 py-2 text-left">{t('table.archive')}</th>}
                <th className="px-3 py-2 text-right">{t('table.size')}</th>
                <th className="px-3 py-2 text-left">{t('table.when')}</th>
                {canDownload && <th className="px-3 py-2 text-right">{t('table.action')}</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
              {rows.map((row, i) => {
                const id = `${row.tenant_id}:${row.key ?? `${row.last_modified ?? ''}:${i}`}`;
                return (
                  <tr key={id} data-testid={`backup-${id}`}>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{row.subdomain}</td>
                    {canDownload && (
                      <td className="px-3 py-2 font-mono text-xs text-gray-600 dark:text-gray-400">{row.key?.split('/').pop()}</td>
                    )}
                    <td className="px-3 py-2 text-right tabular-nums text-gray-600 dark:text-gray-400">{fmtSize(row.size_bytes, t)}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">
                      {fmtDateTime(row.last_modified)}
                    </td>
                    {canDownload && (
                      <td className="px-3 py-2 text-right">
                        {row.key && (
                          <button
                            type="button"
                            disabled={downloading === id}
                            onClick={() => void download(row, id)}
                            className="rounded border border-gray-300 dark:border-gray-700 px-2.5 py-1 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
                          >
                            {downloading === id ? t('downloading') : t('download')}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
